import PropTypes from "prop-types";
import React from "react";
import { Redirect } from "react-router-dom";
import * as QS from "util/query-string";

/*
 * Flexible location controller responsible for a single url query parameter
 * hydration.
 *
 * To control multiple parameters, nest the controllers. If controllers are
 * sibling, I'm not sure how we can ensure the redirects occur correctly.
 */
class QsParamController extends React.PureComponent {
  static get propTypes() {
    return {
      children: PropTypes.node,
      location: PropTypes.object.isRequired,
      // The query parameter supports multiple values, ie tags=1&tags=3&tags=8
      multi: PropTypes.bool,
      // Do not override existing query parameters.
      noOverride: PropTypes.bool,
      // Only assert query parameter on component mount.
      onMount: PropTypes.bool,
      qsName: PropTypes.string.isRequired,
      qsValue: PropTypes.string.isRequired,
    };
  }

  static get defaultProps() {
    return {
      multi: false,
      noOverride: false,
      onMount: false,
    };
  }

  constructor(props) {
    super(props);

    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  _calcRedirect() {
    const { location, multi, noOverride, onMount, qsName, qsValue } =
      this.props;
    const { pathname, search } = location;
    const params = QS.parse(search);

    let shouldRedirect = false;
    let redirectPath = null;

    if (onMount && this._isMounted) {
      return null;
    }

    if (qsName in params) {
      // The query parameter name exists.
      const curValue = params[qsName];
      if (noOverride) {
        // Do nothing.
      } else if (multi) {
        // Multiple values allowed, ensure our value exists and determine
        // whether redirect is needed. ie: tags=1&tags=2&tags=9
        const parsed = Array.isArray(curValue) ? curValue : [curValue];

        const values = new Set(parsed);
        if (!values.has(qsValue)) {
          shouldRedirect = true;
        }

        values.add(qsValue);
        params[qsName] = Array.from(values).sort();
      } else if (curValue !== qsValue) {
        // Single value enforced, only redirect if it's not there.
        params[qsName] = qsValue;
        shouldRedirect = true;
      }
    } else {
      // The query parameter name does not exist. Add it.
      params[qsName] = qsValue;
      shouldRedirect = true;
    }

    if (shouldRedirect) {
      // We're gonna redirect, build the redirect target url.
      const search = QS.stringify(params);
      redirectPath = `${pathname}?${search}`;
    }

    return redirectPath;
  }

  render() {
    const redirectPath = this._calcRedirect();
    if (redirectPath) {
      return <Redirect to={redirectPath} />;
    }

    return this.props.children;
  }
}

export { QsParamController };
